import { Link, useNavigate } from "react-router-dom"
import { useEffect, useState } from "react"
import { DashboardSidebar } from "@/components/dashboard-sidebar"
import { Button } from "@/components/ui/button"
import { Card } from "@/components/ui/card"
import { Input } from "@/components/ui/input"
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "@/components/ui/dialog"
import { User, Bell, HelpCircle, X, Mail, Edit2 } from "lucide-react"
import { NAVIGATION_ROUTES } from '@/constants/routes'
import { logout, getCurrentUser, sendChangeEmailCode, changeEmail } from '@/api/auth'

export default function Settings() {
  const navigate = useNavigate()
  const [user, setUser] = useState<any>(null)
  const [loading, setLoading] = useState(true)
  const [showChangeEmailDialog, setShowChangeEmailDialog] = useState(false)
  const [newEmail, setNewEmail] = useState("")
  const [code, setCode] = useState("")
  const [sendingCode, setSendingCode] = useState(false)
  const [countdown, setCountdown] = useState(0)
  const [changing, setChanging] = useState(false)
  const [error, setError] = useState("")
  const [success, setSuccess] = useState("")

  useEffect(() => {
    loadUserInfo()
  }, [])

  const loadUserInfo = async () => {
    try {
      setLoading(true)
      const userData = await getCurrentUser()
      setUser(userData)
    } catch (err: any) {
      console.error("获取用户信息失败:", err)
    } finally {
      setLoading(false)
    }
  }

  const handleLogout = async () => {
    await logout()
    navigate(NAVIGATION_ROUTES.HOME)
  }

  const handleOpenChangeEmail = () => {
    setShowChangeEmailDialog(true)
    setNewEmail("")
    setCode("")
    setError("")
    setSuccess("")
    setCountdown(0)
  }

  const handleSendCode = async () => {
    if (!newEmail || !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(newEmail)) {
      setError("请输入有效的邮箱地址")
      return
    }

    if (newEmail === user?.email) {
      setError("新邮箱不能与当前邮箱相同")
      return
    }

    setError("")
    setSendingCode(true)
    try {
      await sendChangeEmailCode({ newEmail })
      setSuccess("验证码已发送到新邮箱，请查收")
      setCountdown(60)
      
      // 倒计时
      const timer = setInterval(() => {
        setCountdown((prev) => {
          if (prev <= 1) {
            clearInterval(timer)
            return 0
          }
          return prev - 1
        })
      }, 1000)
    } catch (err: any) {
      setError(err.message || "发送验证码失败")
    } finally {
      setSendingCode(false)
    }
  }

  const handleChangeEmail = async () => {
    if (!newEmail || !code) {
      setError("请输入新邮箱和验证码")
      return
    }

    if (code.length !== 6) {
      setError("验证码必须是6位数字")
      return
    }

    setError("")
    setChanging(true)
    try {
      await changeEmail({ newEmail, code })
      setSuccess("邮箱更换成功")
      // 重新加载用户信息
      await loadUserInfo()
      // 2秒后关闭对话框
      setTimeout(() => {
        setShowChangeEmailDialog(false)
      }, 2000)
    } catch (err: any) {
      setError(err.message || "更换邮箱失败")
    } finally {
      setChanging(false)
    }
  }

  return (
    <div className="flex min-h-screen bg-gray-50">
      {/* 左侧边栏 */}
      <DashboardSidebar />

      {/* 右侧内容区 */}
      <div className="flex-1 flex flex-col">
        {/* 顶部标题栏 */}
        <div className="bg-white border-b border-gray-200 px-8 py-4">
          <div className="flex items-center justify-between">
            {/* 左侧标题 */}
            <h1 className="text-xl font-bold text-gray-900">个人中心</h1>

            {/* 右侧操作按钮 */}
            <div className="flex items-center gap-3">
              <Button variant="ghost" size="sm" className="text-gray-700 hover:text-gray-900 h-auto p-0 text-sm">
                升级会员
              </Button>
              <Button variant="ghost" size="icon" className="text-gray-600 hover:text-gray-900 h-8 w-8">
                <HelpCircle className="w-4 h-4" />
              </Button>
              <Button variant="ghost" size="icon" className="text-gray-600 hover:text-gray-900 relative h-8 w-8">
                <Bell className="w-4 h-4" />
                <span className="absolute top-1 right-1 w-2 h-2 bg-red-500 rounded-full" />
              </Button>
              <Button variant="ghost" size="icon" className="text-gray-600 hover:text-gray-900 h-8 w-8">
                <User className="w-4 h-4" />
              </Button>
              <Link to={NAVIGATION_ROUTES.HOME}>
                <Button variant="ghost" size="icon" className="text-gray-600 hover:text-gray-900 h-8 w-8">
                  <X className="w-4 h-4" />
                </Button>
              </Link>
            </div>
          </div>
        </div>

        {/* 主内容区 */}
        <main className="flex-1 p-8 bg-gray-50">
          <div className="max-w-6xl mx-auto">
            <div className="grid lg:grid-cols-[1fr_400px] gap-8">
              {/* Left Column - Account Info */}
              <div className="space-y-6">
                <Card className="bg-white rounded-xl border border-gray-200 p-6">
                  <h2 className="text-xl font-bold text-gray-900 mb-6">账号信息</h2>

                  <div className="space-y-4">
                    <div className="flex items-center justify-between py-3 border-b border-gray-100">
                      <div className="flex items-center gap-2">
                        <Mail className="w-4 h-4 text-gray-400" />
                        <span className="text-sm text-gray-600">绑定邮箱</span>
                      </div>
                      <div className="flex items-center gap-3">
                        {loading ? (
                          <span className="text-sm text-gray-400">加载中...</span>
                        ) : user?.email ? (
                          <>
                            <span className="text-sm text-gray-900">{user.email}</span>
                            <Button 
                              variant="link" 
                              className="text-blue-600 text-sm p-0 h-auto flex items-center gap-1"
                              onClick={handleOpenChangeEmail}
                            >
                              <Edit2 className="w-3 h-3" />
                              更换
                            </Button>
                          </>
                        ) : (
                          <Button 
                            variant="link" 
                            className="text-blue-600 text-sm p-0 h-auto"
                            onClick={handleOpenChangeEmail}
                          >
                            绑定
                          </Button>
                        )}
                      </div>
                    </div>

                    <div className="flex items-center justify-between py-3 border-b border-gray-100">
                      <span className="text-sm text-gray-600">绑定微信账号</span>
                      <Button variant="link" className="text-blue-600 text-sm p-0 h-auto">
                        绑定
                      </Button>
                    </div>

                    <div className="flex items-center justify-between py-3 border-b border-gray-100">
                      <span className="text-sm text-gray-600">手机号</span>
                      <div className="flex items-center gap-3">
                        {loading ? (
                          <span className="text-sm text-gray-400">加载中...</span>
                        ) : user?.phone ? (
                          <>
                            <span className="text-sm text-gray-900">{user.phone}</span>
                            <Button
                              variant="link"
                              className="text-blue-600 text-sm p-0 h-auto"
                              type="button"
                              onClick={() => {
                                // 预留“更换绑定手机号”入口
                                alert("更换手机号功能即将上线，请稍后再试。")
                              }}
                            >
                              更换绑定
                            </Button>
                          </>
                        ) : (
                          <Button
                            variant="link"
                            className="text-blue-600 text-sm p-0 h-auto"
                            type="button"
                            onClick={() => {
                              // 预留“绑定手机号”入口
                              alert("绑定手机号功能即将上线，请稍后再试。")
                            }}
                          >
                            绑定
                          </Button>
                        )}
                      </div>
                    </div>

                    <div className="flex items-center justify-between py-3 border-b border-gray-100">
                      <span className="text-sm text-gray-600">注销账号</span>
                      <Button variant="link" className="text-red-600 text-sm p-0 h-auto">
                        注销
                      </Button>
                    </div>

                    <div className="pt-3">
                      <Button 
                        variant="outline" 
                        className="text-gray-700 border-gray-300 bg-transparent"
                        onClick={handleLogout}
                      >
                        登出
                      </Button>
                    </div>
                  </div>
                </Card>
              </div>

              {/* Right Column - Pro Info */}
              <div className="space-y-6">
                <Card className="bg-white rounded-xl border border-gray-200 p-6">
                  <div className="flex items-center justify-between mb-6">
                    <h2 className="text-xl font-bold text-gray-900">
                      <span className="bg-black text-white px-2 py-1 rounded text-sm mr-2">Pro</span>
                      账号信息
                    </h2>
                    <Button className="bg-yellow-400 hover:bg-yellow-500 text-gray-900 rounded-lg px-6">
                      立即升级
                    </Button>
                  </div>

                  <div className="space-y-4 mb-6">
                    <div>
                      <p className="text-sm font-medium text-gray-900 mb-1">账号信息:</p>
                      <p className="text-sm text-gray-600">账号: 免费</p>
                      <p className="text-sm text-gray-600">到期日期: 无</p>
                    </div>
                  </div>

                 {/*  <div className="space-y-4">
                    <div className="border-t border-gray-100 pt-4">
                      <div className="flex items-start gap-2 mb-3">
                        <Lock className="w-4 h-4 text-yellow-500 mt-0.5" />
                        <h3 className="text-sm font-medium text-gray-900">定制专属域名</h3>
                      </div>
                      <p className="text-sm text-gray-600 mb-2">kickass-brand.cowtransfer.com</p>
                      <p className="text-xs text-gray-500 leading-relaxed">
                        自定义域名专属后，互联网上其他使用该名称的人都能访问到您的主页，作为新用户，需要以域名的名称为主体进行注册。
                      </p>
                    </div>

                    <div className="border-t border-gray-100 pt-4">
                      <div className="flex items-start gap-2 mb-3">
                        <Lock className="w-4 h-4 text-yellow-500 mt-0.5" />
                        <h3 className="text-sm font-medium text-gray-900">定制专属传输背景</h3>
                      </div>
                      <p className="text-xs text-gray-500 mb-3">
                        最多支持三个背景，每个背景可独立设置链接，每 45 秒切换一次
                        <br />
                        我们建议这图片/视频最大为20MB，以便完成的面板及显示
                        <br />
                        推荐分辨率：1920px * 1080px
                      </p>
                      <div className="grid grid-cols-3 gap-3">
                        {[1, 2, 3].map((i) => (
                          <div key={i} className="aspect-video bg-gray-200 rounded-lg flex items-center justify-center">
                            <Lock className="w-6 h-6 text-gray-400" />
                          </div>
                        ))}
                      </div>
                      <p className="text-xs text-gray-500 mt-3 leading-relaxed">
                        自定义背景设置后，登录作者页面进行审核，审核通过后，专属上传页会显示上传的背景，审核时间1至3个工作日，审核结果将会通知到站内信息。
                      </p>
                    </div>

                    <div className="border-t border-gray-100 pt-4">
                      <div className="flex items-start gap-2">
                        <Lock className="w-4 h-4 text-yellow-500 mt-0.5" />
                        <div>
                          <h3 className="text-sm font-medium text-gray-900 mb-1">Pro 成员设置</h3>
                          <p className="text-sm text-gray-600">需成为团队账号</p>
                        </div>
                      </div>
                    </div>
                  </div> */}
                </Card>
              </div>
            </div>
          </div>
        </main>
      </div>

      {/* 更换邮箱对话框 */}
      <Dialog open={showChangeEmailDialog} onOpenChange={setShowChangeEmailDialog}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>更换邮箱</DialogTitle>
            <DialogDescription>
              请输入新邮箱地址，我们将发送验证码到新邮箱
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4 py-4">
            {user?.email && (
              <div className="text-sm text-gray-600">
                当前邮箱: <span className="font-medium text-gray-900">{user.email}</span>
              </div>
            )}

            <div>
              <label htmlFor="newEmail" className="block text-sm font-medium text-gray-700 mb-2">
                新邮箱地址
              </label>
              <Input
                id="newEmail"
                type="email"
                placeholder="请输入新邮箱地址"
                value={newEmail}
                onChange={(e) => setNewEmail(e.target.value)}
                className="h-10"
                disabled={changing}
              />
            </div>

            <div>
              <label htmlFor="code" className="block text-sm font-medium text-gray-700 mb-2">
                验证码
              </label>
              <div className="flex gap-2">
                <Input
                  id="code"
                  type="text"
                  placeholder="请输入6位验证码"
                  value={code}
                  onChange={(e) => {
                    const value = e.target.value.replace(/\D/g, '').slice(0, 6)
                    setCode(value)
                  }}
                  className="h-10 flex-1"
                  maxLength={6}
                  disabled={changing}
                />
                <Button
                  type="button"
                  variant="outline"
                  onClick={handleSendCode}
                  disabled={sendingCode || countdown > 0 || !newEmail}
                  className="h-10"
                >
                  {sendingCode
                    ? "发送中..."
                    : countdown > 0
                    ? `${countdown}秒`
                    : "发送验证码"}
                </Button>
              </div>
            </div>

            {error && (
              <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg text-sm">
                {error}
              </div>
            )}

            {success && (
              <div className="bg-green-50 border border-green-200 text-green-700 px-4 py-3 rounded-lg text-sm">
                {success}
              </div>
            )}

            <div className="flex justify-end gap-3 pt-2">
              <Button
                variant="outline"
                onClick={() => setShowChangeEmailDialog(false)}
                disabled={changing}
              >
                取消
              </Button>
              <Button
                onClick={handleChangeEmail}
                disabled={changing || !newEmail || !code || code.length !== 6}
                className="bg-blue-600 hover:bg-blue-700"
              >
                {changing ? "更换中..." : "确认更换"}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  )
}
